import { forwardRef } from "react";
import { useTranslation } from "react-i18next";

import { useRootSelector } from "store";

import { useAssets } from "../../hooks/useAssets";
import {
  getCardName,
  getWalletName,
  maskString,
  PAYMENT_METHOD_TYPE,
  typeMapper,
} from "../../utility/utils";

import { PaymentData } from "store/payments/payments-state";
import { activeAccountTypeConfig } from "store/account/account-selector";

import classes from "./QuickPayPayment.module.scss";

export interface QuickPayProps {
  paymentData?: PaymentData;
  accountType?: string;
}
export const QuickPayPayment = forwardRef((props: QuickPayProps, ref: any) => {
  const { t } = useTranslation(["common", "receipt", "payment"]);
  const { user } = useRootSelector((state) => state.user);
  const { paymentData } = props;
  const { createAssetUrl } = useAssets();
  const config = useRootSelector(activeAccountTypeConfig);
  const { client } = useRootSelector((state) => state.config);

  const amountPaid =
    paymentData?.paymentResponse?.amountPaid! ||
    +paymentData?.paymentAmount! + +paymentData?.feeTotal!;

  const feeTotal = paymentData?.paymentResponse?.feeTotal
    ? paymentData?.paymentResponse?.feeTotal
    : paymentData?.feeTotal;

  const displayName =
    paymentData?.firstName && paymentData?.lastName
      ? `${paymentData?.firstName} ${paymentData?.lastName}`
      : `${user?.givenName} ${user?.familyName}`;

  const getPaymentSourceLabel = () => {
    const { paymentMethodType, paymentMethodName } =
      paymentData?.paymentResponse || {};

    if (paymentMethodType) {
      if (paymentMethodType === PAYMENT_METHOD_TYPE.eWallet) {
        return getWalletName(paymentData?.paymentMethod?.walletType || 0);
      }

      const reversedType = typeMapper(
        paymentMethodType,
        "paymentMethodTypeReverse",
      );
      return t(`common:${reversedType}_label`);
    }

    if (paymentMethodName) {
      return t(`common:${paymentMethodName.toLowerCase()}_label`);
    }

    return "-";
  };

  return (
    <div ref={ref} className={classes.Wrapper}>
      <div className={classes.Header}>
        <div className={classes.Header_Logo_Wrapper}>
          <div className={classes.Header_Logo_Container}>
            <img
              className={classes.Logo}
              src={createAssetUrl("logo-v-print")}
              alt="logo"
            />
          </div>
        </div>
      </div>
      <div className={classes.Heading_Wrapper}>
        <p className={classes.Title}>
          {t("receipt:reciept_from", {
            value: client.name,
          })}
        </p>
        <p className={classes.Reciept_Number}>
          {t("receipt:reciept_no", {
            no: paymentData?.paymentResponse?.confirmationNumber,
          })}
        </p>
      </div>

      <div className={classes.Account_Detail_Container}>
        <div className={classes.Detail_Content_Item}>
          <p className={classes.Item_label}>{t("receipt:customer_name")}</p>
          <p>{displayName}</p>
        </div>
        <div className={classes.Detail_Content_Item}>
          <p className={classes.Item_label}>
            {t("receipt:account_identifier")}
          </p>
          <p>{paymentData?.paymentResponse?.accountNumber}</p>
        </div>
        <div className={classes.Detail_Content_Item}>
          <p className={classes.Item_label}>{t("payment:account_type")}</p>
          <p>{config.name}</p>
        </div>
      </div>
      <div className={classes.Summary_Wrapper}>
        <p className={classes.Detail_Container_Heading}>
          {t("receipt:summary")}
        </p>
        <div className={classes.Detail_Content}>
          {paymentData?.paymentType ? (
            <div className={classes.Detail_Content_Item}>
              <p className={classes.Item_label}>{t("receipt:payment_type")}</p>
              <p className={classes.Capatilize}>
                {paymentData?.paymentType &&
                  config?.paymentTypes.find(
                    (pt) => pt.code === paymentData?.paymentType,
                  )?.name}
              </p>
            </div>
          ) : null}
          <hr />
          {paymentData?.paymentResponse?.paymentDate ? (
            <div className={classes.Detail_Content_Item}>
              <p className={classes.Item_label}>{t("payment:paymentDate")}</p>
              <p>
                {t("common:date_format", {
                  value: paymentData?.paymentResponse?.paymentDate,
                })}
              </p>
            </div>
          ) : null}
          <hr />
          {paymentData?.paymentResponse?.paymentMethodName ||
          paymentData?.paymentResponse?.paymentMethodType ? (
            <div className={classes.Detail_Content_Item}>
              <p className={classes.Item_label}>
                {t("receipt:payment_source")}
              </p>
              <p className={classes.Capatilize}>{getPaymentSourceLabel()}</p>
            </div>
          ) : null}
          {paymentData?.paymentMethod?.bankAccountType ? (
            <div className={classes.Detail_Content_Item}>
              <p
                className={[classes.Item_label, classes.Padding_left].join(" ")}
              >
                {t("receipt:source_type")}
              </p>
              <p className={classes.Capatilize}>
                {paymentData?.paymentMethod.bankAccountType &&
                  typeMapper(
                    paymentData?.paymentMethod.bankAccountType,
                    "accountReverse",
                  )}
              </p>
            </div>
          ) : null}
          {paymentData?.paymentMethod?.cardType ? (
            <div className={classes.Detail_Content_Item}>
              <p
                className={[classes.Item_label, classes.Padding_left].join(" ")}
              >
                {t("receipt:source_type")}
              </p>
              <p className={classes.Capatilize}>
                {paymentData.paymentMethod.cardType &&
                  getCardName(paymentData.paymentMethod.cardType)}
              </p>
            </div>
          ) : null}
          {paymentData?.paymentMethod?.accountNumber ? (
            <div className={classes.Detail_Content_Item}>
              <p className={classes.Item_label}>{t("payment:accountNumber")}</p>
              <p>
                {paymentData?.paymentMethod.accountNumber
                  ? maskString(paymentData?.paymentMethod.accountNumber, 4)
                  : ""}
              </p>
            </div>
          ) : paymentData?.paymentMethod?.lastFour ? (
            <div className={classes.Detail_Content_Item}>
              <p className={classes.Item_label}>
                {t("payment_next:card_number_label")}
              </p>
              <p>
                {paymentData?.paymentMethod.lastFour
                  ? maskString(paymentData?.paymentMethod.lastFour, 4)
                  : ""}
              </p>
            </div>
          ) : null}
          <hr />
          {amountPaid ? (
            <div className={classes.Detail_Content_Item}>
              <p className={[classes.Item_label, classes.Bold].join(" ")}>
                {t("payment:amountPaid")}
              </p>
              <p className={[classes.Bold].join(" ")}>
                {t("common:currency_format", {
                  value: amountPaid,
                })}
              </p>
            </div>
          ) : null}
          {paymentData?.paymentResponse?.amountDue ? (
            <div className={classes.Detail_Content_Item}>
              <p
                className={[classes.Item_label, classes.Padding_left].join(" ")}
              >
                {t("common:amount_due")}
              </p>
              <p>
                {t("common:currency_format", {
                  value: paymentData.paymentResponse?.amountDue,
                })}
              </p>
            </div>
          ) : null}
          {feeTotal ? (
            <div className={classes.Detail_Content_Item}>
              <p
                className={[classes.Item_label, classes.Padding_left].join(" ")}
              >
                {t("receipt:included_fee")}
              </p>
              <p>
                {t("common:currency_format", {
                  value: feeTotal,
                })}
              </p>
            </div>
          ) : null}
          {paymentData?.paymentResponse?.note ? (
            <>
              <hr />
              <div
                className={[
                  classes.Detail_Content_Item,
                  classes.Detail_Content_Item_fullRow,
                ].join(" ")}
              >
                <p className={classes.Item_label}>{t("receipt:note_label")}</p>
                <p className={classes.Padding_left}>
                  {paymentData.paymentResponse?.note}
                </p>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* <div className={classes.Footer}>
        <p className={classes.Footer_disclosure}>
          {t("footer:footer_disclosure")}
        </p>
      </div> */}
    </div>
  );
});
